import { Spinner } from "@/components/ui/Spinner";
import { useLayoutPreference } from "@/features/layout/LayoutPreferenceProvider";
import { classNames } from "@/lib/format";

const FULL_WIDTH_LABEL = "Volle Seitenbreite";

type FullWidthToggleProps = {
  className?: string;
  /** `menu` = dezent im Profil-Dropdown. */
  variant?: "menu" | "settings";
};

/** Schalter für volle Inhaltsbreite – Persistenz serverseitig pro Benutzer. */
export function FullWidthToggle({ className, variant = "menu" }: FullWidthToggleProps) {
  const { fullWidth, setFullWidth, loading } = useLayoutPreference();

  const control = loading ? (
    <Spinner className="h-5 w-5" />
  ) : (
    <span className="relative inline-flex h-5 w-9 shrink-0 items-center">
      <input
        type="checkbox"
        className="peer sr-only"
        checked={fullWidth}
        onChange={(event) => setFullWidth(event.target.checked)}
        aria-label={FULL_WIDTH_LABEL}
      />
      <span
        className={classNames(
          "absolute inset-0 rounded-full transition",
          "bg-fill peer-checked:bg-accent",
        )}
        aria-hidden="true"
      />
      <span
        className={classNames(
          "absolute left-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition",
          "peer-checked:translate-x-4",
        )}
        aria-hidden="true"
      />
    </span>
  );

  if (variant === "settings") {
    return (
      <label
        className={classNames(
          "flex cursor-pointer items-start justify-between gap-4 px-5 py-4",
          className,
        )}
      >
        <div className="min-w-0 space-y-1">
          <span className="block text-sm font-medium text-label">{FULL_WIDTH_LABEL}</span>
          <span className="block text-sm text-label-secondary">
            Der Inhaltsbereich unterhalb der Navigation nutzt die volle Breite. Vertikale Menüs
            behalten ihren Platz am linken Rand.
          </span>
        </div>
        <span className="relative mt-0.5 inline-flex h-5 w-9 shrink-0 items-center justify-center">
          {control}
        </span>
      </label>
    );
  }

  return (
    <section className={classNames("eh-account-section", className)}>
      <label className="flex cursor-pointer select-none items-center justify-between gap-3">
        <span className="min-w-0">
          <span className="eh-account-section-title mb-0 block">{FULL_WIDTH_LABEL}</span>
          <span className="mt-0.5 block text-xs text-label-tertiary">
            Inhalt über die volle Breite – mit Platz für vertikale Menüs
          </span>
        </span>
        {control}
      </label>
    </section>
  );
}
